import React, { useEffect } from "react";
import { InputNumber, Select } from "antd";
import moment from "moment";

import FormSection from "./FormSection";
import Field from "./Field";
import Notice from "./Notice";
import { Calendar } from "./icons";
import styles from "./fundForm.module.scss";

/** `Enums.Frequency` — how a withdrawal window's interval is counted. */
export const WindowUnit = {
  Daily: 1,
  Weekly: 2,
  Monthly: 3,
} as const;

const UNIT_OPTIONS = [
  { value: WindowUnit.Daily, label: "Days" },
  { value: WindowUnit.Weekly, label: "Weeks" },
  { value: WindowUnit.Monthly, label: "Months" },
];

/** The backend caps the interval differently per unit. */
const MAX_INTERVAL: Record<number, number> = {
  [WindowUnit.Daily]: 365,
  [WindowUnit.Weekly]: 52,
  [WindowUnit.Monthly]: 12,
};

const MOMENT_UNIT: Record<number, moment.unitOfTime.DurationConstructor> = {
  [WindowUnit.Daily]: "days",
  [WindowUnit.Weekly]: "weeks",
  [WindowUnit.Monthly]: "months",
};

const WINDOWS_SHOWN = 3;

export type TWithdrawalWindow = {
  interval?: number | null;
  unit?: number | null;
  gracePeriod?: number | null;
};

interface IWithdrawalWindowSection {
  value: TWithdrawalWindow;
  onChange: (next: TWithdrawalWindow) => void;
  disabled?: boolean;
  step?: string;
}

/**
 * When a Habit Save customer can take money out.
 *
 * Three numbers that are hard to picture, so the section shows the dates they
 * produce. Those are the same dates the mobile app lists on the last step of
 * Habit Save, which makes a mistake here obvious rather than latent.
 */
export default function WithdrawalWindowSection({
  value,
  onChange,
  disabled = false,
  step,
}: IWithdrawalWindowSection) {
  const unit = value.unit ?? WindowUnit.Monthly;

  /**
   * A default that is shown has to be a default that is stored.
   *
   * The unit fell back to Monthly for display only, so the select read
   * "Months" while the payload carried `withdrawalWindowIntervalUnit: null`
   * and creation came back "Withdrawal window interval unit is required for
   * habit save investment products". Anything the form shows as chosen is
   * chosen.
   */
  useEffect(() => {
    if (value.unit === null || value.unit === undefined) {
      onChange({ ...value, unit: WindowUnit.Monthly });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.unit]);
  const interval = value.interval ?? undefined;
  const grace = value.gracePeriod ?? undefined;
  const max = MAX_INTERVAL[unit];

  const update = (patch: TWithdrawalWindow) => onChange({ ...value, ...patch });

  const start = moment();
  const windows =
    interval && interval > 0
      ? Array.from({ length: WINDOWS_SHOWN }, (_unused, i) => {
          const opens = start
            .clone()
            .add(interval * (i + 1), MOMENT_UNIT[unit]);

          return {
            opens,
            // The window covers `grace` days counting from the day it opens.
            closes: opens.clone().add(Math.max((grace ?? 1) - 1, 0), "days"),
          };
        })
      : [];

  const range = (a: moment.Moment, b: moment.Moment) =>
    a.isSame(b, "day")
      ? a.format("D MMM YYYY")
      : a.isSame(b, "month")
        ? `${a.format("D")} – ${b.format("D MMM YYYY")}`
        : `${a.format("D MMM")} – ${b.format("D MMM YYYY")}`;

  return (
    <FormSection
      id="withdrawal-windows"
      title="Withdrawal windows"
      subtitle="Habit Save locks funds between windows. These settings decide when a customer can take money out."
      icon={<Calendar color="#713fff" />}
      action={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className={styles.tabChip}>Habit Save only</span>
          {step ? <span className={styles.sectionMeta}>{step}</span> : null}
        </div>
      }
    >
      <div className={styles.gridThirds}>
        <Field
          required
          label="A window opens every"
          hint={`Up to ${max} counting this way.`}
        >
          <div style={{ display: "flex", gap: 12 }}>
            <InputNumber
              aria-label="Withdrawal window interval"
              style={{ width: 96 }}
              min={1}
              max={max}
              disabled={disabled}
              value={interval}
              onChange={(next) =>
                update({ interval: next === null ? null : Number(next) })
              }
            />
            <Select
              aria-label="Withdrawal window unit"
              style={{ flexGrow: 1 }}
              size="large"
              disabled={disabled}
              value={unit}
              options={UNIT_OPTIONS}
              onChange={(next) => update({ unit: Number(next) })}
            />
          </div>
        </Field>

        <Field
          required
          label="And stays open for"
          hint="Counted from the day it opens."
        >
          <InputNumber
            aria-label="Grace period"
            style={{ width: "100%" }}
            min={0}
            suffix="days"
            disabled={disabled}
            value={grace}
            onChange={(next) =>
              update({ gracePeriod: next === null ? null : Number(next) })
            }
          />
        </Field>

        {windows.length ? (
          // Carries a label of its own so it sits on the same rhythm as the
          // controls beside it — without one the cell aligned to the bottom of
          // the row, hint text included, and floated below them.
          <Field label="First window" hint="For a plan opened today.">
            <div
              style={{
                height: 56,
                display: "flex",
                alignItems: "center",
                padding: "0 16px",
                borderRadius: 8,
                background: "#F8F5FF",
                border: "1px solid #E4DAFF",
                fontSize: 16,
                fontWeight: 500,
                color: "#3F137D",
              }}
            >
              {windows[0].opens.format("D MMM YYYY")}
            </div>
          </Field>
        ) : null}

        <div className={`${styles.span3} ${styles.rule}`} />

        <div className={styles.span3}>
          {windows.length ? (
            <>
              <div className={styles.previewTitle}>
                The first {WINDOWS_SHOWN} windows, for a plan opened today
              </div>

              <div className={styles.timeline}>
                <div className={styles.timelineTrack} />
                <div className={styles.timelineStops}>
                  <div className={styles.timelineStop}>
                    <span
                      className={styles.timelineDot}
                      style={{ background: "#FFFFFF", borderColor: "#16054E" }}
                    />
                    <div style={{ textAlign: "center" }}>
                      <div
                        className={styles.timelineLabel}
                        style={{ color: "#16054E" }}
                      >
                        Plan starts
                      </div>
                      <div className={styles.timelineDate}>
                        {start.format("D MMM YYYY")}
                      </div>
                    </div>
                  </div>

                  {windows.map((window, index) => {
                    const faded = index === windows.length - 1;
                    const shade = faded ? "#C7B6FF" : "#713FFF";

                    return (
                      <div
                        key={window.opens.toISOString()}
                        className={styles.timelineStop}
                      >
                        <span
                          className={styles.timelineDot}
                          style={{ background: shade, borderColor: shade }}
                        />
                        <div style={{ textAlign: "center" }}>
                          <div
                            className={styles.timelineLabel}
                            style={{ color: faded ? "#6B5AA0" : "#3F137D" }}
                          >
                            Window {index + 1}
                          </div>
                          <div className={styles.timelineDate}>
                            {range(window.opens, window.closes)}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <div className={styles.timelineStop}>
                    <span
                      className={styles.timelineDot}
                      style={{ background: "#E7EAEF", borderColor: "#E7EAEF" }}
                    />
                    <div style={{ textAlign: "center" }}>
                      <div
                        className={styles.timelineDate}
                        style={{ marginTop: 2 }}
                      >
                        and onward
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 28 }}>
                <Notice tone="info">
                  These are the dates the customer sees on the last step of
                  Habit Save in the mobile app, so the two should agree.
                </Notice>
              </div>
            </>
          ) : (
            <Notice tone="info">
              Set an interval to see the windows a customer would get.
            </Notice>
          )}
        </div>
      </div>
    </FormSection>
  );
}
